import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { Loader2, CheckCircle2, FileText, Trophy, AlertCircle, ArrowLeft } from "lucide-react";

const PrelimsTestSeriesPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [testType, setTestType] = useState("mini"); // Default to mini test for faster generation
  const [difficulty, setDifficulty] = useState("mixed");
  const [subjects, setSubjects] = useState(["Polity", "Economy", "Geography", "History", "Environment"]);
  const [result, setResult] = useState(null);
  const [userAnswers, setUserAnswers] = useState({});
  const [showAnswers, setShowAnswers] = useState(false);

  // Auth guard - redirect if logged out during test
  useEffect(() => {
    if (!user || !token) {
      navigate('/auth', { replace: true });
    }
  }, [user, token, navigate]);

  const isPro = user?.role === "PRO";

  const allSubjects = [
    "Polity",
    "Economy",
    "Geography",
    "History",
    "Environment",
    "Science & Technology",
    "Internal Security",
    "Current Affairs"
  ];

  const handleSubjectToggle = (subject) => {
    if (subjects.includes(subject)) {
      setSubjects(subjects.filter(s => s !== subject));
    } else {
      setSubjects([...subjects, subject]);
    }
  };

  const pollTaskStatus = async (taskId) => {
    const token = localStorage.getItem("token");
    const maxAttempts = 60; // Poll for up to 60 attempts (2 minutes with 2s intervals)
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await managedAxios.get(
          `${API_BASE}/prelims/test/status/${taskId}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        const { status, progress, result, error } = response.data;

        // Update progress
        if (status === "processing" || status === "pending") {
          toast({
            description: `Generating test... ${progress}% complete`
          });
        }

        // Check if completed
        if (status === "completed" && result) {
          setResult(result);
          toast({
            description: `✅ Generated ${result.num_questions}-question test successfully!`
          });
          setLoading(false);
          return;
        }

        // Check if failed
        if (status === "failed") {
          throw new Error(error || "Test generation failed");
        }

        // Continue polling
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000); // Poll every 2 seconds
        } else {
          throw new Error("Test generation timed out. Please try again.");
        }
      } catch (err) {
        console.error("Polling error:", err);
        toast({
          variant: "destructive",
          title: "Test Generation Failed",
          description: err.message || "Failed to generate test"
        });
        setLoading(false);
      }
    };

    poll();
  };

  const handleGenerate = async () => {
    if (subjects.length === 0) {
      toast({
        variant: "destructive",
        description: "Please select at least one subject"
      });
      return;
    }

    setLoading(true);
    setResult(null);
    setUserAnswers({});
    setShowAnswers(false);

    const token = localStorage.getItem("token");
    const actualTestType = isPro ? testType : "mini";

    try {
      const response = await managedAxios.post(
        `${API_BASE}/prelims/test/generate`,
        {
          test_type: actualTestType,
          subjects,
          difficulty
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000
        }
      );

      // Check if response is immediate (small test) or async (large test)
      if (response.data.task_id) {
        // Async task - start polling
        toast({
          description: response.data.message || "Test generation started..."
        });
        pollTaskStatus(response.data.task_id);
      } else {
        // Immediate response
        setResult(response.data);
        toast({
          description: `Generated ${response.data.num_questions}-question test!`
        });
        setLoading(false);
      }
    } catch (err) {
      console.error("Error generating test:", err);
      
      let errorMessage = "Failed to generate test";
      if (err.code === 'ECONNABORTED') {
        errorMessage = "Request timeout. Please try with Mini Test for faster generation.";
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      toast({
        variant: "destructive",
        title: "Test Generation Failed",
        description: errorMessage
      });
      setLoading(false);
    }
  };

  const handleAnswerSelect = (questionIdx, answer) => {
    setUserAnswers({
      ...userAnswers,
      [questionIdx]: answer
    });
  };

  const handleSubmit = () => {
    setShowAnswers(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const calculateScore = () => {
    if (!result?.questions) return { correct: 0, total: 0, percentage: 0 };
    let correct = 0;
    result.questions.forEach((q, idx) => {
      if (userAnswers[idx] === q.correct_answer) {
        correct++;
      }
    });
    const percentage = Math.round((correct / result.questions.length) * 100);
    return { correct, total: result.questions.length, percentage };
  };

  const score = showAnswers ? calculateScore() : null;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
      {/* Back Button */}
      <Button variant="outline" onClick={() => navigate('/prelims')} className="mb-4">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Prelims Hub
      </Button>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Test Series Generator</h1>
          <p className="text-sm text-slate-600">
            Generate UPSC Prelims full-length tests
          </p>
        </div>
        <Badge className={isPro ? "bg-amber-500" : "bg-slate-200"}>
          {isPro ? "⭐ PRO" : "🔓 FREE"}
        </Badge>
      </div>

      {/* Configuration Card */}
      {!result && (
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="testType">Test Type</Label>
                <select
                  id="testType"
                  value={testType}
                  onChange={(e) => setTestType(e.target.value)}
                  disabled={loading || !isPro}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md"
                >
                  <option value="mini">Mini Mock (5 Questions)</option>
                  <option value="full">Full Test (100 Questions)</option>
                </select>
                {!isPro && (
                  <p className="text-xs text-amber-600 mt-1">PRO: Full test available</p>
                )}
              </div>

              <div>
                <Label htmlFor="difficulty">Difficulty</Label>
                <select
                  id="difficulty"
                  value={difficulty}
                  onChange={(e) => setDifficulty(e.target.value)}
                  disabled={loading}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md"
                >
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                  <option value="mixed">Mixed (UPSC-realistic)</option>
                </select>
              </div>
            </div>

            <div>
              <Label>Select Subjects</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                {allSubjects.map((subject) => (
                  <div key={subject} className="flex items-center space-x-2">
                    <Checkbox
                      id={subject}
                      checked={subjects.includes(subject)}
                      onCheckedChange={() => handleSubjectToggle(subject)}
                      disabled={loading}
                    />
                    <label
                      htmlFor={subject}
                      className="text-sm text-slate-700 cursor-pointer"
                    >
                      {subject}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <Button
              onClick={handleGenerate}
              disabled={loading}
              className="w-full bg-gradient-to-r from-rose-500 to-pink-600"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating Test...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4 mr-2" />
                  Generate Test
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Score Display */}
      {showAnswers && score && (
        <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
          <CardContent className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div>
                <Trophy className="w-8 h-8 mx-auto mb-2 text-amber-500" />
                <p className="text-sm text-slate-600 mb-1">Your Score</p>
                <p className="text-3xl font-bold text-indigo-600">
                  {score.correct}/{score.total}
                </p>
              </div>
              <div>
                <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-500" />
                <p className="text-sm text-slate-600 mb-1">Accuracy</p>
                <p className="text-3xl font-bold text-green-600">
                  {score.percentage}%
                </p>
              </div>
              <div>
                <AlertCircle className="w-8 h-8 mx-auto mb-2 text-red-500" />
                <p className="text-sm text-slate-600 mb-1">Incorrect</p>
                <p className="text-3xl font-bold text-red-600">
                  {score.total - score.correct}
                </p>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-indigo-200">
              <p className="text-center text-sm text-slate-700">
                {score.percentage >= 80 ? "🎉 Excellent! Keep it up!" :
                 score.percentage >= 60 ? "👍 Good job! Review weak areas." :
                 score.percentage >= 40 ? "📚 Need more practice. Focus on concepts." :
                 "💪 Don't give up! Strengthen your basics."}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Test Questions */}
      {result?.questions && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-slate-900">
              {result.test_name} ({result.num_questions} Questions)
            </h2>
            {!showAnswers && (
              <Badge variant="outline">
                {Object.keys(userAnswers).length}/{result.questions.length} Attempted
              </Badge>
            )}
          </div>

          {/* Subject Distribution (PRO) */}
          {isPro && result.subject_distribution && (
            <Card className="bg-slate-50 border-slate-200">
              <CardContent className="p-4">
                <p className="text-xs font-semibold text-slate-900 mb-2">Subject Distribution:</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(result.subject_distribution).map(([subject, count]) => (
                    <Badge key={subject} variant="secondary" className="text-xs">
                      {subject}: {count}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Questions */}
          {result.questions.map((q, idx) => {
            const userAnswer = userAnswers[idx];
            const isCorrect = userAnswer === q.correct_answer;
            const isAttempted = userAnswer !== undefined;

            return (
              <Card
                key={idx}
                className={
                  showAnswers && isAttempted
                    ? isCorrect
                      ? "border-green-300 bg-green-50"
                      : "border-red-300 bg-red-50"
                    : ""
                }
              >
                <CardContent className="p-6 space-y-4">
                  {/* Question Header */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1">
                      <Badge variant="outline" className="shrink-0">
                        Q{idx + 1}
                      </Badge>
                      <div className="flex-1">
                        <p className="text-base text-slate-900 font-medium mb-2">
                          {q.question}
                        </p>
                        {isPro && q.subject && (
                          <div className="flex gap-2">
                            <Badge variant="secondary" className="text-xs">
                              {q.subject}
                            </Badge>
                            {q.difficulty && (
                              <Badge
                                className={`text-xs ${
                                  q.difficulty === "Hard" ? "bg-red-500" :
                                  q.difficulty === "Medium" ? "bg-orange-500" : "bg-green-500"
                                }`}
                              >
                                {q.difficulty}
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Options */}
                  <div className="space-y-2">
                    {Object.entries(q.options).map(([key, value]) => {
                      const isSelected = userAnswer === key;
                      const isCorrectOption = key === q.correct_answer;

                      return (
                        <button
                          key={key}
                          onClick={() => !showAnswers && handleAnswerSelect(idx, key)}
                          disabled={showAnswers}
                          className={`w-full text-left p-3 rounded-lg border transition-all ${
                            showAnswers
                              ? isCorrectOption
                                ? "bg-green-100 border-green-400"
                                : isSelected
                                ? "bg-red-100 border-red-400"
                                : "bg-white border-slate-200"
                              : isSelected
                              ? "bg-indigo-50 border-indigo-400"
                              : "bg-white border-slate-200 hover:border-indigo-300"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold">{key})</span>
                              <span>{value}</span>
                            </div>
                            {showAnswers && (
                              <>
                                {isCorrectOption && (
                                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                                )}
                                {isSelected && !isCorrectOption && (
                                  <AlertCircle className="w-5 h-5 text-red-600" />
                                )}
                              </>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Explanation (shown after submission) */}
                  {showAnswers && q.explanation && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-xs font-semibold text-blue-900 mb-1">
                        Correct Answer: {q.correct_answer}
                      </p>
                      <p className="text-sm text-slate-700">{q.explanation}</p>
                      {isPro && q.topic && (
                        <p className="text-xs text-slate-600 mt-2">
                          <span className="font-semibold">Topic:</span> {q.topic}
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {/* Submit Button */}
          {!showAnswers && (
            <Button
              onClick={handleSubmit}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-600"
              size="lg"
            >
              Submit Test & Check Answers
            </Button>
          )}

          {/* Reset Button */}
          {showAnswers && (
            <div className="flex gap-4">
              <Button
                onClick={() => {
                  setResult(null);
                  setUserAnswers({});
                  setShowAnswers(false);
                }}
                variant="outline"
                className="flex-1"
              >
                Generate New Test
              </Button>
              <Button
                onClick={() => {
                  setUserAnswers({});
                  setShowAnswers(false);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-600"
              >
                Retry Same Test
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PrelimsTestSeriesPage;
